import React, { createContext, useContext, useState, ReactNode } from 'react';
import { MenuItem } from '../types/menu';
import { trackAddToCart, trackRemoveFromCart, trackCartCleared } from '../utils/analytics';

interface CartItem extends MenuItem {
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  menuId: string | null;
  setMenuId: (menuId: string) => void;
  addItem: (item: MenuItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (itemId: string) => number;
  getTotalItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [menuId, setMenuId] = useState<string | null>(null);

  const addItem = (item: MenuItem) => {
    setItems(currentItems => {
      const existingItem = currentItems.find(i => i.id === item.id);
      if (existingItem) {
        // Item already exists, increment quantity
        const updatedItems = currentItems.map(i =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
        
        // Track the quantity increase
        const updatedItem = updatedItems.find(i => i.id === item.id);
        if (updatedItem && menuId) {
          trackAddToCart({
            id: updatedItem.id,
            name: updatedItem.name || (updatedItem.props?.translations?.en?.name?.text ?? updatedItem.props?.enLocalization?.name ?? 'Unknown Item'),
            price: Number(updatedItem.price) || 0,
            quantity: 1, // Only track the quantity being added
            category: updatedItem.props?.enCategory?.text || updatedItem.category
          }, menuId);
        }
        
        return updatedItems;
      }
      
      // New item being added
      const normalizedItem = {
        ...item,
        name: item.name || (item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? 'Unknown Item'),
        quantity: 1
      };
      
      // Track the new item addition
      if (menuId) {
        trackAddToCart({
          id: normalizedItem.id,
          name: normalizedItem.name,
          price: Number(normalizedItem.price) || 0,
          quantity: 1,
          category: normalizedItem.props?.enCategory?.text || normalizedItem.category
        }, menuId);
      }
      
      return [...currentItems, normalizedItem];
    });
  };

  const removeItem = (itemId: string) => {
    setItems(currentItems => {
      const itemToRemove = currentItems.find(item => item.id === itemId);
      if (itemToRemove && menuId) {
        // Track the item removal
        trackRemoveFromCart({
          id: itemToRemove.id,
          name: itemToRemove.name || (itemToRemove.props?.translations?.en?.name?.text ?? itemToRemove.props?.enLocalization?.name ?? 'Unknown Item'),
          price: Number(itemToRemove.price) || 0,
          quantity: itemToRemove.quantity,
          category: itemToRemove.props?.enCategory?.text || itemToRemove.category
        }, menuId);
      }
      return currentItems.filter(item => item.id !== itemId);
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    
    setItems(currentItems => {
      const currentItem = currentItems.find(item => item.id === itemId);
      if (!currentItem) return currentItems;
      
      const quantityDifference = quantity - currentItem.quantity;
      
      const updatedItems = currentItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      );
      
      // Track the quantity change
      if (menuId) {
        if (quantityDifference > 0) {
          // Quantity increased
          trackAddToCart({
            id: currentItem.id,
            name: currentItem.name || (currentItem.props?.translations?.en?.name?.text ?? currentItem.props?.enLocalization?.name ?? 'Unknown Item'),
            price: Number(currentItem.price) || 0,
            quantity: quantityDifference,
            category: currentItem.props?.enCategory?.text || currentItem.category
          }, menuId);
        } else if (quantityDifference < 0) {
          // Quantity decreased
          trackRemoveFromCart({
            id: currentItem.id,
            name: currentItem.name || (currentItem.props?.translations?.en?.name?.text ?? currentItem.props?.enLocalization?.name ?? 'Unknown Item'),
            price: Number(currentItem.price) || 0,
            quantity: Math.abs(quantityDifference),
            category: currentItem.props?.enCategory?.text || currentItem.category
          }, menuId);
        }
      }
      
      return updatedItems;
    });
  };

  const clearCart = () => {
    setItems(currentItems => {
      // Track cart clearing with all items
      if (currentItems.length > 0 && menuId) {
        trackCartCleared(currentItems.map(item => ({
          id: item.id,
          name: item.name || (item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? 'Unknown Item'),
          price: Number(item.price) || 0,
          quantity: item.quantity,
          category: item.props?.enCategory?.text || item.category
        })), menuId);
      }
      return [];
    });
  };

  const getItemQuantity = (itemId: string) => {
    return items.find(item => item.id === itemId)?.quantity || 0;
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        menuId,
        setMenuId,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getItemQuantity,
        getTotalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 